/**
 * AskStatusCard — what the pill on a desk opens (owner feedback on prn512):
 * when the ask was sent, each recipient's state and media, reminders so far,
 * the answer once there is one, and three actions: nudge the people still
 * waiting, open the thread, withdraw the ask.
 */
import { useState } from 'react';

import { trackEvent } from '../../services/portalTracking';
import {
  discardDataRequest,
  dismissDataRequest,
  markDataRequestSeen,
  nudgeDataRequest,
  sendAskAnyway,
  type DoorDataRequestRead,
} from '../../services/qcpDataRequests';
import { whenLabel, type DeskAskGroup } from '../../pages/quality-triage/merlinBrief';
import { canSendAnyway, handoffWords } from './askKind';
import styles from './AskStatusCard.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface AskStatusCardProps {
  threadNo: string;
  group: DeskAskGroup;
  onClose: () => void;
  onRequestUpdated: (updated: DoorDataRequestRead) => void;
  /** Scroll to the asks section (the full card with the reply thread). */
  onOpenThread: () => void;
}

const first = (name: string) => name.split(' ')[0] || name;

const SOURCE_WORD: Record<string, string> = {
  customer_relay: 'relays to the customer',
  'role:sales_rep': "account's sales rep",
  'role:cs_rep': "account's CS rep",
};

function sourceLabel(source: string): string {
  if (SOURCE_WORD[source]) return SOURCE_WORD[source];
  if (source.startsWith('team:fn_')) return 'owns this desk';
  if (source.startsWith('team:')) return 'on the team';
  return '';
}

export function AskStatusCard({ threadNo, group, onClose, onRequestUpdated, onOpenThread }: AskStatusCardProps) {
  // A read-only viewer sees the ask's state and sends nothing (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nudged, setNudged] = useState(false);

  const waiting = group.recipients.filter((r) => r.state === 'waiting');
  const handed = group.recipients.filter((r) => r.state === 'handed');
  const replied = group.recipients.filter((r) => r.state === 'replied');
  const heldBack = handed.filter((r) => canSendAnyway(r.request));
  const latestAnswer = (r: DeskAskGroup['recipients'][number]) =>
    r.request.responses.length > 0 ? r.request.responses[r.request.responses.length - 1] : null;

  const run = async (label: string, work: () => Promise<DoorDataRequestRead[]>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await work();
      updated.forEach(onRequestUpdated);
      trackEvent('action', label, {
        thread_no: threadNo,
        group_id: group.id,
        step_no: group.stepNo,
        recipients: updated.length,
        data_request_ids: updated.map((u) => u.id),
      });
      return true;
    } catch {
      setError('That did not go through. Try again in a moment.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const nudge = async () => {
    const ok = await run('qcp_ask_nudge', async () => {
      const out: DoorDataRequestRead[] = [];
      for (const r of waiting) out.push(await nudgeDataRequest(r.id));
      return out;
    });
    if (ok) setNudged(true);
  };

  const withdraw = async () => {
    const ok = await run('qcp_ask_withdraw', async () => {
      const out: DoorDataRequestRead[] = [];
      for (const r of waiting) out.push(await dismissDataRequest(r.id, 'withdrawn by Jess from Do next'));
      // A pass-along held for Merlin's check is still a draft underneath;
      // one that went out is dismissed like an ask (kanban 196e9d9b).
      for (const r of handed) {
        out.push(
          r.request.status === 'proposed'
            ? await discardDataRequest(r.id, 'pass-along withdrawn by Jess')
            : await dismissDataRequest(r.id, 'pass-along withdrawn by Jess from Do next'),
        );
      }
      return out;
    });
    if (ok) onClose();
  };

  const sendAnyway = async () => {
    await run('qcp_pass_along_send_anyway', async () => {
      const out: DoorDataRequestRead[] = [];
      for (const r of heldBack) out.push(await sendAskAnyway(r.id));
      return out;
    });
  };

  // Seen is per answer and only when the reader says so (proposal 1): it
  // used to fire for every reply on hover and on "Open the thread".
  const seen = (requestId: string) =>
    run('answers_mark_seen', async () => [await markDataRequestSeen(requestId)]);

  const headline =
    group.state === 'replied'
      ? `Replied — ${replied.map((r) => first(r.name)).join(', ')}`
      : group.state === 'waiting'
        ? `Asked ${waiting.length === 1 ? first(waiting[0].name) : `${waiting.length} people`}`
        : group.state === 'handed'
          ? `Passed to ${handed.length === 1 ? first(handed[0].name) : `${handed.length} people`}`
          : group.state === 'withdrawn'
            ? 'Withdrawn'
            : 'Closed';
  const passAlong = group.state === 'handed' || handed.length > 0;
  const reminders = Math.max(0, ...group.recipients.map((r) => r.followups - 1));

  return (
    <div className={styles.back} onClick={onClose} data-testid="ask-status">
      <div className={styles.card} role="dialog" aria-modal="true" aria-labelledby="ask-status-title" onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <div>
            <div className={styles.eyebrow}>{passAlong ? 'This pass-along' : 'This ask'} · {threadNo}{group.stepNo != null && ` · step ${group.stepNo}`}</div>
            <h2 id="ask-status-title" className={styles.title}>{headline}</h2>
            <div className={styles.sub}>{group.title}</div>
          </div>
          <button
            type="button"
            className={styles.x}
            onClick={onClose}
            aria-label="Close"
            data-action="qcp_ask_status_close"
            data-outcome="close"
          >
            ×
          </button>
        </div>
        <ul className={styles.rows}>
          <li>
            <span className={styles.k}>Sent</span>
            <span>{whenLabel(group.sentAt)}</span>
          </li>
          {group.recipients.map((r) => (
            <li key={r.id} data-testid="ask-status-recipient">
              <span className={styles.k}>{r.name}</span>
              <span>
                <span className={styles.state} data-s={r.state}>
                  {r.state === 'waiting' && `waiting${r.lastFollowupAt && r.followups > 1 ? ` · reminded ${whenLabel(r.lastFollowupAt)}` : ''}`}
                  {r.state === 'handed' && handoffWords(r.request.handoff, first(r.name))}
                  {r.state === 'replied' &&
                    (latestAnswer(r)?.channel === 'found'
                      ? `answered elsewhere ${whenLabel(r.repliedAt)} — Merlin found it`
                      : `replied ${whenLabel(r.repliedAt)}`)}
                  {r.state === 'told' && `told: ${first(r.toldBy ?? '')} already answered, nothing more needed`}
                  {r.state === 'withdrawn' && 'withdrawn'}
                  {r.state === 'closed' && 'closed'}
                </span>
                {sourceLabel(r.source) && <span className={styles.media}> · {sourceLabel(r.source)}</span>}
              </span>
            </li>
          ))}
          <li>
            <span className={styles.k}>Reminders</span>
            <span>
              {passAlong
                ? 'none: a pass-along is sent once; it is theirs now, and a reply lands here if one comes'
                : `${reminders > 0 ? `${reminders} sent` : 'none yet'}${nudged ? ' · one more on its way' : ''} · Merlin reminds on its own until it is answered`}
            </span>
          </li>
        </ul>
        {replied.map((r) => {
          const answer = latestAnswer(r);
          return answer ? (
            <div key={r.id} className={styles.answer}>
              <b>{answer.channel === 'found' ? 'Merlin found' : first(r.name)}:</b> {answer.body_text}
              {r.unseen && !readOnly && (
                <div>
                  <button
                    type="button"
                    className={styles.ghost}
                    onClick={() => void seen(r.id)}
                    disabled={busy}
                  >
                    Mark {first(r.name)}’s answer seen
                  </button>
                </div>
              )}
            </div>
          ) : null;
        })}
        {error && (
          <p className={styles.error} role="alert">{error}</p>
        )}
        <div className={styles.actions}>
          {!readOnly && waiting.length > 0 && (
            <button type="button" className={styles.send} onClick={() => void nudge()} disabled={busy || nudged}>
              {nudged
                ? 'Nudged'
                : waiting.length === 1
                  ? `Nudge ${first(waiting[0].name)} now`
                  : waiting.length === 2
                    ? `Nudge ${first(waiting[0].name)} and ${first(waiting[1].name)} now`
                    : `Nudge all ${waiting.length} now`}
            </button>
          )}
          {!readOnly && heldBack.length > 0 && (
            <button type="button" className={styles.send} onClick={() => void sendAnyway()} disabled={busy} data-action="qcp_pass_along_send_anyway">
              Send anyway
            </button>
          )}
          <button type="button" className={styles.ghost} onClick={onOpenThread} disabled={busy}>
            Open the thread
          </button>
          {!readOnly && (waiting.length > 0 || handed.length > 0) && (
            <button type="button" className={`${styles.ghost} ${styles.danger}`} onClick={() => void withdraw()} disabled={busy}>
              {passAlong ? 'Withdraw it' : 'Withdraw the ask'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AskStatusCard;
